/**
 * CRM MAINTENANCE ASLANE V3
 * Bootstrap : vérifie l'authentification et charge le router
 */

import { api } from './api-client.js';
import { initOfflineUI } from './modules/offline-sync.js';
import { initWidgetsNavigation } from './modules/widgets-navigation.js';
import './utils/toast.js'; // side-effect: attache window.showToast (cf. AUDIT_BUGS_UI.md §0.2)

// Lazy loaders : router + OCR ne sont chargés qu'au moment où l'utilisateur en a besoin
// → bundle initial réduit (Module 14.3, code-splitting esbuild)
const _lazyInitRouter = () => import('./modules/router.js').then((m) => m.initRouter());
const _lazyOcr = () => import('./modules/nameplate-ocr.js');

console.log('🚀 app.js chargé');

// Fallback global : window.showClientDetail navigue vers la fiche client
// même si clients.js / clients-detail.js n'a pas encore été lazy-chargé.
// Avant ce fix : ~30 boutons "👤 Client" morts sur /installations, /sav-en-cours,
// /clients-en-panne (cf. AUDIT_BUGS_UI.md §0.1). Les modules clients-detail.js et
// clients.js peuvent overrider cette implémentation par la leur (plus riche)
// quand ils sont chargés.
if (typeof window.showClientDetail !== 'function') {
  window.showClientDetail = function (clientId) {
    if (clientId) window.location.hash = '#/clients/' + clientId;
  };
}

// Auto-fermeture des modales `.m9-modal-overlay` par Escape + click sur l'overlay.
// Composant utilisé par m9-planning-gantt, m9-absences, et déclenché aussi
// depuis Trésorerie (Charges fixes "+ Ajouter"). Avant ce fix : modales
// non-fermables par Escape ni overlay-click (cf. AUDIT_BUGS_UI.md §0.3).
(function wireM9ModalAutoClose() {
  if (typeof document === 'undefined') return;
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const ovs = document.querySelectorAll('.m9-modal-overlay');
    if (ovs.length) {
      try { ovs[ovs.length - 1].remove(); } catch (_) {}
    }
  });
  try {
    new MutationObserver((muts) => {
      for (const m of muts) {
        for (const n of m.addedNodes) {
          if (n && n.nodeType === 1 && n.classList && n.classList.contains('m9-modal-overlay')) {
            n.addEventListener('click', (ev) => {
              if (ev.target === n) {
                try { n.remove(); } catch (_) {}
              }
            });
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  } catch (_) { /* MutationObserver indispo : feature dégradée, pas grave */ }
})();

try { initWidgetsNavigation(); } catch (e) { console.warn('[widgets-nav] init failed', e); }

window.onerror = function(msg, src, line, col, err) {
  console.error('🔥 [GLOBAL ERROR]', msg, src, line, err);
  const app = document.getElementById('app');
  if (app && (!app.innerHTML || app.innerHTML.trim().length < 50)) {
    app.innerHTML = '<div style="padding:2rem;text-align:center;"><h2 style="color:#dc2626;">⚠️ Erreur inattendue</h2><p>' + String(msg) + '</p><button onclick="location.reload()" style="margin-top:1rem;padding:0.5rem 1.5rem;background:#dc2626;color:#fff;border:none;border-radius:8px;cursor:pointer;">Recharger</button></div>';
  }
  return false;
};

window.onunhandledrejection = function(event) {
  console.error('🔥 [UNHANDLED PROMISE]', event.reason);
};

// Fonction pour cacher le splash
function hideSplash() {
  const splash = document.getElementById('splash');
  if (splash) {
    splash.style.display = 'none';
  }
}

// Cacher le splash immédiatement si le DOM est prêt
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(hideSplash, 100);
  });
} else {
  setTimeout(hideSplash, 100);
}

// Cacher le splash après un délai de sécurité (max 3 secondes)
setTimeout(() => {
  hideSplash();
  // Si le splash est toujours visible après 3 secondes, forcer l'affichage d'un message
  const splash = document.getElementById('splash');
  if (splash && splash.style.display !== 'none') {
    const app = document.getElementById('app');
    if (app) {
      app.innerHTML = `
        <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 2rem;">
          <div style="background: white; padding: 2rem; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); text-align: center; max-width: 560px;">
            <h2 style="color: #dc2626; margin-bottom: 1rem;">⚠️ Problème de chargement</h2>
            <p style="margin-bottom: 1rem;">Le chargement prend plus de temps que prévu. Veuillez vérifier votre connexion.</p>
            <button onclick="location.reload()" style="padding: 0.75rem 1.5rem; background: #dc2626; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: 600;">
              Recharger la page
            </button>
          </div>
        </div>
      `;
    }
    splash.style.display = 'none';
  }
}, 3000);

function showFatalError(message, details) {
  console.error('❌ Erreur fatale:', message, details || '');
  hideSplash();
  const app = document.getElementById('app');
  if (!app) return;
  app.innerHTML = `
    <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 2rem;">
      <div style="background: white; padding: 2rem; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); text-align: center; max-width: 560px;">
        <h2 style="color: #dc2626; margin-bottom: 1rem;">⚠️ Erreur de chargement</h2>
        <p style="margin-bottom: 1rem;">${String(message || 'Erreur lors du chargement du CRM')}</p>
        <pre style="text-align:left; background:#f8fafc; padding: 0.75rem; border-radius: 8px; overflow:auto; max-height: 200px; font-size: 12px; color:#0f172a;">${String(details || '')}</pre>
        <button onclick="location.reload()" style="padding: 0.75rem 1.5rem; background: #dc2626; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: 600;">
          Recharger la page
        </button>
      </div>
    </div>
  `;
}

function initApp() {
  console.log('⏳ Initialisation app...');
  try {
    const token = localStorage.getItem('auth_token');
    const user = localStorage.getItem('user');

    if (token && user) {
      // Utilisateur connecté, initialiser le router
      hideSplash();
      api.setToken(token);
      window.api = api;
      fetch('/api/v1/auth/upload-cookie', { headers: { 'Authorization': 'Bearer ' + token } }).catch(() => {});
      try {
        // Vérifier et corriger le hash si nécessaire
        const currentHash = window.location.hash.slice(1);
        console.log('📍 Hash actuel au démarrage:', currentHash);
        
        // Si le hash est vide ou invalide, forcer le dashboard
        if (!currentHash || currentHash === '' || currentHash === '/login') {
          console.log('🔄 Redirection vers dashboard (hash vide ou login)');
          window.location.hash = '#/';
        }
        
        _lazyInitRouter().catch((e) => {
          console.error('Erreur initialisation router (lazy):', e);
          showFatalError('Erreur de chargement du router', e?.message || String(e));
        });
        initOfflineUI();
        // OCR pré-chargé en arrière-plan (non bloquant) pour les techniciens
        _lazyOcr().catch(() => {});
        console.log('✅ Application prête');
      } catch (routerError) {
        console.error('Erreur initialisation router:', routerError);
        hideSplash();
        showFatalError('Erreur de chargement du router', routerError?.message || String(routerError));
      }
      return;
    }

    // Pas connecté, afficher le login
    hideSplash();
    renderLogin();
    console.log('✅ Application prête');
  } catch (err) {
    console.error('Erreur initApp:', err);
    hideSplash();
    showFatalError('Erreur de chargement du CRM', err && (err.stack || err.message) ? (err.stack || err.message) : String(err));
  }
}

// Intercept clicks on upload links to add auth token
document.addEventListener('click', function(e) {
  const link = e.target.closest('a[href*="/uploads/"]');
  if (!link) return;
  const href = link.getAttribute('href');
  if (!href || href.includes('token=')) return;
  const token = localStorage.getItem('auth_token');
  if (!token) return;
  e.preventDefault();
  const sep = href.includes('?') ? '&' : '?';
  window.open(href + sep + 'token=' + encodeURIComponent(token), '_blank');
}, true);

// Patch window.open to add auth token for upload URLs
const _origOpen = window.open;
window.open = function(url, target, features) {
  if (url && typeof url === 'string' && url.includes('/uploads/') && !url.includes('token=')) {
    const token = localStorage.getItem('auth_token');
    if (token) {
      const sep = url.includes('?') ? '&' : '?';
      url = url + sep + 'token=' + encodeURIComponent(token);
    }
  }
  return _origOpen.call(window, url, target, features);
};

// Bootstrap (sans async/await)
initApp();

function renderLogin() {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = `
    <div id="login-page" style="min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 2rem; background: #1e293b;">
      <div style="width: 100%; max-width: 400px; background: #0f172a; border-radius: 16px; padding: 2rem; box-shadow: 0 4px 12px rgba(0,0,0,0.3); border: 2px solid #dc2626;">
        <div style="text-align: center; margin-bottom: 2rem;">
          <img src="images/logo-aslane.jpg" alt="ASLANE" style="max-width: 120px; height: auto; margin-bottom: 1rem; display: block; margin-left: auto; margin-right: auto;" onerror="this.style.display='none'">
          <h1 style="color: #ffffff !important; margin-bottom: 0.5rem; font-size: 24px; font-weight: 900;">CRM Maintenance</h1>
          <p style="color: #93c5fd !important; font-size: 14px;">ASLANE</p>
        </div>
        <form id="loginForm">
          <div style="margin-bottom: 1rem;">
            <label style="display: block; font-size: 14px; font-weight: 700; color: #ffffff; margin-bottom: 0.5rem;">Email</label>
            <input 
              type="email" 
              id="email" 
              required 
              autocomplete="email"
              placeholder="gestion@aslane.fr"
              style="width: 100%; padding: 12px; border: 2px solid #dc2626; border-radius: 8px; font-size: 16px; min-height: 44px; box-sizing: border-box; background: #1e293b; color: #ffffff;"
            >
          </div>
          <div style="margin-bottom: 1.5rem;">
            <label style="display: block; font-size: 14px; font-weight: 700; color: #ffffff; margin-bottom: 0.5rem;">Mot de passe</label>
            <input 
              type="password" 
              id="password" 
              required 
              autocomplete="current-password"
              placeholder="••••••••"
              style="width: 100%; padding: 12px; border: 2px solid #dc2626; border-radius: 8px; font-size: 16px; min-height: 44px; box-sizing: border-box; background: #1e293b; color: #ffffff;"
            >
          </div>
          <div id="loginError" style="color: #dc2626; display: none; margin-bottom: 1rem; text-align: center; font-size: 14px;"></div>
          <button 
            type="submit" 
            style="width: 100%; padding: 14px; background: #dc2626; color: white; border: none; border-radius: 8px; font-size: 18px; font-weight: 700; cursor: pointer; min-height: 48px;"
          >
            Se connecter
          </button>
        </form>
      </div>
    </div>
  `;

  const form = document.getElementById('loginForm');
  if (!form) return;

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const email = document.getElementById('email').value;
    const password = document.getElementById('password').value;
    const errorDiv = document.getElementById('loginError');
    const submitBtn = form.querySelector('button[type="submit"]');

    submitBtn.disabled = true;
    submitBtn.textContent = 'Connexion...';
    if (errorDiv) errorDiv.style.display = 'none';

    const apiBase = window.location.hostname === 'localhost'
      ? 'http://localhost:3003/api/v1'
      : '/api/v1';

    fetch(`${apiBase}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
      .then((res) => {
        if (!res.ok) {
          return res.json().catch(() => ({})).then((payload) => {
            const msg = payload && payload.message ? String(payload.message) : `Erreur ${res.status}`;
            throw new Error(msg);
          });
        }
        return res.json();
      })
      .then((data) => {
        const token = data.access_token || data.accessToken;
        const userData = data.user || {};

        console.log('🔐 [LOGIN SUCCESS] Saving credentials');
        localStorage.setItem('auth_token', token);
        localStorage.setItem('user', JSON.stringify(userData));

        // DEBUG: Verify save
        const savedToken = localStorage.getItem('auth_token');
        console.log('🔐 [LOGIN] Token saved to localStorage:', savedToken ? savedToken.substring(0, 40) + '...' : 'ERROR: NOT SAVED');

        // Appliquer le thème après login
        if (window.applyTheme) {
          window.applyTheme();
        }

        // CRITICAL: Ensure token is set in API client IMMEDIATELY
        api.setToken(token);
        console.log('🔐 [LOGIN] api.token set:', api.token ? api.token.substring(0, 40) + '...' : 'ERROR: NOT SET');
        
        window.api = api;

        // Initialiser le router et afficher le dashboard
        // FORCER la redirection vers le dashboard principal AVANT d'initialiser le router
        console.log('🔄 [LOGIN] Redirecting to dashboard...');
        window.location.hash = '#/';
        
        setTimeout(() => {
          console.log('🔄 [LOGIN] Calling initRouter() (lazy)');
          console.log('🔐 [LOGIN] api.token BEFORE initRouter:', api.token ? 'SET ✅' : 'NOT SET ❌');
          _lazyInitRouter().catch((e) => console.error('Erreur initRouter (lazy):', e));
        }, 50);
      })
      .catch((err) => {
        console.error('Erreur login:', err);
        if (errorDiv) {
          errorDiv.textContent = err && err.message ? err.message : 'Erreur de connexion';
          errorDiv.style.display = 'block';
        }
        submitBtn.disabled = false;
        submitBtn.textContent = 'Se connecter';
      });
  });
}
window.renderLogin = renderLogin;

// ==================== FONCTIONS GLOBALES INTERVENTIONS ====================
// Ces fonctions doivent être accessibles partout (dashboard, planning, liste interventions)

window.updateInterventionStatus = async function (id, newStatus, event) {
  // Récupérer l'élément select de manière plus fiable
  let selectElement = null;
  if (event && event.target && event.target.tagName === 'SELECT') {
    selectElement = event.target;
  } else {
    // Fallback : chercher le select par son attribut data-old-status
    selectElement = document.querySelector(`select[data-old-status][onchange*="${id}"]`);
  }
  
  // Récupérer l'ancien statut
  const oldStatus = selectElement?.getAttribute('data-old-status') || selectElement?.value || '';
  
  if (!confirm(`Changer le statut de l'intervention vers "${newStatus}" ?`)) {
    // Remettre l'ancien statut dans le select
    if (selectElement && oldStatus) {
      selectElement.value = oldStatus;
    }
    return;
  }
  
  try {
    console.log('🔄 Mise à jour statut intervention:', { id, newStatus });
    const result = await api.updateIntervention(id, { status: newStatus });
    console.log('✅ Statut mis à jour avec succès:', result);
    alert('✅ Statut mis à jour');
    // Recharger la page actuelle
    window.location.reload();
  } catch (error) {
    console.error('❌ Erreur mise à jour statut:', error);
    alert(`Erreur: ${error?.message || 'Impossible de mettre à jour le statut'}`);
    // Remettre l'ancien statut dans le select en cas d'erreur
    if (selectElement && oldStatus) {
      selectElement.value = oldStatus;
    }
  }
};

// Notification de mise à jour SW : quand le SW v38+ s'active, il envoie SW_UPDATED.
// On affiche un bandeau invitant l'utilisateur à actualiser.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'SW_UPDATED') {
      if (document.getElementById('sw-update-banner')) return;
      const banner = document.createElement('div');
      banner.id = 'sw-update-banner';
      banner.innerHTML = `<div style="position:fixed;bottom:1rem;right:1rem;z-index:99999;background:#1e40af;color:white;padding:0.8rem 1.2rem;border-radius:12px;box-shadow:0 4px 16px rgba(0,0,0,0.35);display:flex;align-items:center;gap:0.8rem;max-width:340px;font-size:0.85rem;"><span>🔄 Nouvelle version disponible</span><button onclick="window.location.reload()" style="background:white;color:#1e40af;border:none;padding:0.35rem 0.85rem;border-radius:8px;font-weight:700;cursor:pointer;font-size:0.82rem;white-space:nowrap;">Actualiser</button><button onclick="this.closest('#sw-update-banner').remove()" style="background:transparent;border:none;color:rgba(255,255,255,0.7);cursor:pointer;font-size:1.1rem;line-height:1;padding:0 0.2rem;">✕</button></div>`;
      document.body.appendChild(banner);
    }
  });
}
